/**
 * RiskBox component module.
 * @module advisor/shared/analyze/RiskBox
 *
 */
import React from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';

import {isAuthorized} from '../../reducers/user';
import Icon from '../Icon';
import DescriptiveText from '../DescriptiveText';
import Stratifi from '../../../advisor/components/partners/Stratifi';

const getFontSize = size => {
  switch (size) {
    case 'lg':
      return '18px';
    default:
      return '14px';
  }
};

const getImageSize = size => {
  switch (size) {
    case 'lg':
      return '70px';
    default:
      return '30px';
  }
};

const _RiskBox = styled.div`
  display: inline-block;
  font-size: ${props => getFontSize(props.size)};
  margin: 5px;
  position: relative;
  text-align: center;
  ${props => props.onClick ? 'cursor: pointer;' : ''}
`;

/**
 * Risk Box component
 * @name RiskBox
 * @visibleName Risk Box
 */
const RiskBox = ({action, riskPartner, riskNumber, size, type, title, margin = '5px', isPolling}) => {
  const renderRisk = () => {
    switch (riskPartner) {
      case 'riskalyze':
        return <Icon height={getImageSize(size)}
                     width={getImageSize(size)}
                     svg={`https://media.circleblack.com/riskalyze/${type || 'portfolio'}/${riskNumber || 0}.svg`}
                     margin={'0'}/>;
      case 'totum':
        return <Icon height={getImageSize(size)}
                     width={getImageSize(size)}
                     svg={`https://media.circleblack.com/totum/white/${(!riskNumber && riskNumber !== 0
                       ? 'null'
                       : riskNumber)}.svg`}
                     margin={'0'}/>;
      case 'stratifi':
        return <Stratifi size={size || 'sm'}
                         dataPoint={{name: 'stratifi', value: riskNumber}}
                         isPolling={isPolling}/>;
      default:
        return null;
    }
  };

  const attributes = {
    size,
    margin,
    onClick: !!action && !isAuthorized(['CPLO']) && (e => {
      e.stopPropagation();
      e.preventDefault();
      action(e);
    }),
  };

  return <_RiskBox {...attributes}>
    {title && <DescriptiveText preset={'paragraph'}>{title}</DescriptiveText>}
    {renderRisk()}
  </_RiskBox>;
};

export default RiskBox;

RiskBox.propTypes = {
  /**
   * The action to take when user clicks on Risk Box. Does not apply to CPLO users.
   */
  action: PropTypes.func,
  /**
   * The Risk Partner
   */
  riskPartner: PropTypes.string,
  /**
   * The Risk Number to display
   */
  riskNumber: PropTypes.number,
  /**
   * The size of Risk Box to display. If not passed in, then defaults to small
   */
  size: PropTypes.string,
  /**
   * The type of Risk Box image to display. If not passed in, then defaults to portfolio
   */
  type: PropTypes.oneOf(['portfolio', 'standard', 'target']),
  /**
   * The title for the component
   */
  title: PropTypes.string,
  /**
   * Optional Margin css defaults to 5px
   */
  margin: PropTypes.string,
  /**
   * Whether the risk partner is currently being polled (Stratifi only)
   */
  isPolling: PropTypes.bool,
};
